import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {

  Alert,

  Box,

  Button,

  ButtonGroup,

  CircularProgress,

  Collapse,

  FormControl,

  FormControlLabel,

  FormHelperText,

  InputLabel,

  MenuItem,

  Paper,

  Select,

  Switch,

  TextField,

  Typography,

} from '@mui/material';

import { Save as SaveIcon, AutoFixHigh as SampleIcon } from '@mui/icons-material';

import api from '../../services/api';

import {

  DEFAULT_POOL_WEARABLE_GATEWAY_CONFIG,

  RECOMMENDED_WEARABLE_GATEWAY_PROFILE_ID,

  getRecommendedWearableGatewaySample,

  getWearableGatewaySampleConfig,

  isWearableGatewayConfigUnset,

  mergePoolWearableGatewayConfig,

  resolveWearableGatewayProfiles,

  WEARABLE_GATEWAY_PROFILES_FALLBACK,

  type PoolWearableGatewayConfig,

  type WearableGatewayFieldDef,

  type WearableGatewayProfileId,

  type WearableGatewayProfileMeta,

} from '../../constants/poolWearableGatewayConfig';



type Props = {

  poolId: number | string | undefined;

  showSnackbar?: (message: string, severity: 'success' | 'error' | 'warning' | 'info') => void;

};



const PoolWearableGatewaySettingsCard: React.FC<Props> = ({ poolId, showSnackbar }) => {

  const [profiles, setProfiles] = useState<WearableGatewayProfileMeta[]>(

    WEARABLE_GATEWAY_PROFILES_FALLBACK

  );

  const [config, setConfig] = useState<PoolWearableGatewayConfig>(DEFAULT_POOL_WEARABLE_GATEWAY_CONFIG);

  const [poolCode, setPoolCode] = useState<string>('');

  const [loading, setLoading] = useState(false);

  const [saving, setSaving] = useState(false);

  const [error, setError] = useState<string | null>(null);

  const [samplePreviewOpen, setSamplePreviewOpen] = useState(true);

  const [samplePreviewProfileId, setSamplePreviewProfileId] =

    useState<WearableGatewayProfileId>(RECOMMENDED_WEARABLE_GATEWAY_PROFILE_ID);



  const activeProfile = useMemo(

    () => profiles.find((p) => p.id === config.profile_id),

    [profiles, config.profile_id]

  );



  const sampleContext = useMemo(

    () => ({ poolCode: poolCode || undefined, poolId }),

    [poolCode, poolId]

  );



  const previewSample = useMemo(() => {

    const sample =

      getWearableGatewaySampleConfig(samplePreviewProfileId, sampleContext) ||

      getRecommendedWearableGatewaySample(sampleContext);

    return sample;

  }, [samplePreviewProfileId, sampleContext]);



  const previewSampleJson = useMemo(

    () =>

      JSON.stringify(

        {

          profile_id: previewSample.profile_id,

          enabled: previewSample.enabled,

          parameters: previewSample.parameters,

          notes: previewSample.notes,

        },

        null,

        2

      ),

    [previewSample]

  );



  const applySampleToForm = useCallback(

    (profileId: WearableGatewayProfileId) => {

      const sample = getWearableGatewaySampleConfig(profileId, sampleContext);

      if (!sample) {

        showSnackbar?.('미사용 프로필에는 샘플 없음', 'info');

        return;

      }

      const meta = profiles.find((p) => p.id === sample.profile_id);

      setConfig(mergePoolWearableGatewayConfig(sample, meta?.fields));

      setSamplePreviewProfileId(sample.profile_id);

      showSnackbar?.('샘플 값을 폼에 채움. 확인 후 저장', 'info');

    },

    [profiles, sampleContext, showSnackbar]

  );



  const load = useCallback(async () => {

    if (!poolId) return;

    setLoading(true);

    setError(null);

    try {

      let list = WEARABLE_GATEWAY_PROFILES_FALLBACK;

      let cfgRes: { config?: Partial<PoolWearableGatewayConfig> } | null = null;

      try {

        const profRes = await api.get<{ profiles?: WearableGatewayProfileMeta[] }>(

          '/api/pools/wearable-gateway-profiles',

          undefined,

          { useCache: false }

        );

        list = resolveWearableGatewayProfiles(profRes?.profiles);

      } catch (profErr) {

        console.warn('wearable gateway profiles API failed, using fallback', profErr);

      }

      try {

        cfgRes = await api.get<{ config: PoolWearableGatewayConfig }>(

          `/api/pools/${poolId}/wearable-gateway-config`,

          undefined,

          { useCache: false }

        );

      } catch (cfgErr) {

        console.error(cfgErr);

        setError('시설 게이트웨이 설정을 불러오기 실패');

      }

      setProfiles(list);

      const raw: Partial<PoolWearableGatewayConfig> = cfgRes?.config ?? {};

      if (raw.pool_code) {

        setPoolCode(String(raw.pool_code));

      }

      const pid = raw.profile_id || 'disabled';

      const meta = list.find((p) => p.id === pid);

      const merged = mergePoolWearableGatewayConfig(raw, meta?.fields);

      setConfig(merged);

      setSamplePreviewOpen(isWearableGatewayConfigUnset(merged));

      if (pid !== 'disabled') {

        setSamplePreviewProfileId(pid as WearableGatewayProfileId);

      }

    } catch (e) {

      console.error(e);

      setProfiles(WEARABLE_GATEWAY_PROFILES_FALLBACK);

      setError('웨어러블 게이트웨이 설정을 불러오기 실패');

    } finally {

      setLoading(false);

    }

  }, [poolId]);



  useEffect(() => {

    load();

  }, [load]);



  const handleProfileChange = (profileId: string) => {

    const meta = profiles.find((p) => p.id === profileId);

    setConfig(

      mergePoolWearableGatewayConfig(

        {

          profile_id: profileId as PoolWearableGatewayConfig['profile_id'],

          enabled: profileId !== 'disabled',

          parameters: {},

          notes: config.notes,

        },

        meta?.fields

      )

    );

    if (profileId !== 'disabled') {

      setSamplePreviewProfileId(profileId as WearableGatewayProfileId);

    }

  };



  const setParam = (key: string, value: string | number | boolean) => {

    setConfig((prev) => ({

      ...prev,

      parameters: { ...prev.parameters, [key]: value },

    }));

  };



  const renderField = (field: WearableGatewayFieldDef) => {

    const val = config.parameters[field.key];

    if (field.type === 'boolean') {

      return (

        <FormControlLabel

          key={field.key}

          control={

            <Switch

              checked={Boolean(val)}

              onChange={(e) => setParam(field.key, e.target.checked)}

            />

          }

          label={field.label}

        />

      );

    }

    const isPassword = field.type === 'password';

    const isMultiline = field.type === 'text';

    return (

      <TextField

        key={field.key}

        fullWidth

        margin="dense"

        label={field.label}

        type={field.type === 'number' ? 'number' : isPassword ? 'password' : 'text'}

        multiline={isMultiline}

        minRows={isMultiline ? 2 : undefined}

        required={Boolean(field.required)}

        helperText={field.helper}

        value={val ?? ''}

        onChange={(e) =>

          setParam(

            field.key,

            field.type === 'number' ? Number(e.target.value) : e.target.value

          )

        }

      />

    );

  };



  const handleSave = async () => {

    if (!poolId) return;

    setSaving(true);

    setError(null);

    try {

      await api.put(`/api/pools/${poolId}`, {

        wearable_gateway_config: {

          profile_id: config.profile_id,

          enabled: config.enabled,

          parameters: config.parameters,

          notes: config.notes,

        },

      });

      api.invalidateGetCache('/api/pools/wearable-gateway');

      api.invalidateGetCache(`/api/pools/${poolId}`);

      showSnackbar?.('웨어러블 게이트웨이 설정을 저장함', 'success');

      await load();

    } catch (e) {

      console.error(e);

      setError('저장에 실패');

      showSnackbar?.('웨어러블 게이트웨이 설정 저장에 실패', 'error');

    } finally {

      setSaving(false);

    }

  };



  if (!poolId) {

    return null;

  }



  const recommendedLabel =

    profiles.find((p) => p.id === RECOMMENDED_WEARABLE_GATEWAY_PROFILE_ID)?.label || 'MQTT 게이트웨이';



  return (

    <Paper sx={{ p: 2, mb: 2 }}>

      <Typography variant="h6" gutterBottom>

        웨어러블 게이트웨이 연동

      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>

        시설마다 제조사·모델에 따라 다른 연결 방식을 <strong>프로필</strong>로 선택하고, 아래{' '}

        <strong>파라미터</strong>만 변경. 프로토콜 소스코드는 UI에 넣지 않으며, 벤더별 해석은

        서버 어댑터가 담당

      </Typography>



      <Alert severity="info" sx={{ mb: 2 }}>

        <Typography variant="subtitle2" gutterBottom>

          기본 샘플 ({recommendedLabel})

        </Typography>

        <Typography variant="body2" sx={{ mb: 1 }}>

          아직 저장된 설정이 없으면 아래 샘플이 펼쳐짐. 호스트·비밀번호·토큰만 현장 값으로 바꿔

          「샘플 적용」 후 저장하면 됨

          {poolCode ? ` (시설 코드: ${poolCode})` : ''}

        </Typography>

        <FormControl size="small" fullWidth sx={{ mb: 1, maxWidth: 360 }}>

          <InputLabel id="wg-sample-profile-label">샘플 프로필</InputLabel>

          <Select

            labelId="wg-sample-profile-label"

            label="샘플 프로필"

            value={samplePreviewProfileId}

            onChange={(e) => setSamplePreviewProfileId(e.target.value as WearableGatewayProfileId)}

          >

            {profiles

              .filter((p) => p.id !== 'disabled')

              .map((p) => (

                <MenuItem key={p.id} value={p.id}>

                  {p.label}

                </MenuItem>

              ))}

          </Select>

        </FormControl>

        <ButtonGroup size="small" sx={{ mb: 1, flexWrap: 'wrap' }}>

          <Button

            startIcon={<SampleIcon />}

            onClick={() => applySampleToForm(samplePreviewProfileId)}

          >

            이 샘플 폼에 적용

          </Button>

          <Button

            onClick={() => applySampleToForm(RECOMMENDED_WEARABLE_GATEWAY_PROFILE_ID)}

          >

            추천 샘플 적용 (MQTT)

          </Button>

          <Button onClick={() => setSamplePreviewOpen((v) => !v)}>

            {samplePreviewOpen ? 'JSON 접기' : 'JSON 펼치기'}

          </Button>

        </ButtonGroup>

        <Collapse in={samplePreviewOpen}>

          <Box

            component="pre"

            sx={{

              m: 0,

              p: 1.5,

              bgcolor: 'action.hover',

              borderRadius: 1,

              fontSize: '0.75rem',

              overflow: 'auto',

              maxHeight: 280,

            }}

          >

            {previewSampleJson}

          </Box>

        </Collapse>

      </Alert>



      {error && (

        <Alert severity="error" sx={{ mb: 2 }}>

          {error}

        </Alert>

      )}

      {loading ? (

        <Box display="flex" justifyContent="center" py={3}>

          <CircularProgress size={28} />

        </Box>

      ) : (

        <>

          <FormControl fullWidth margin="dense" sx={{ mb: 1 }}>

            <InputLabel id="wg-profile-label">연동 프로필</InputLabel>

            <Select

              labelId="wg-profile-label"

              label="연동 프로필"

              value={config.profile_id}

              onChange={(e) => handleProfileChange(String(e.target.value))}

            >

              {profiles.map((p) => (

                <MenuItem key={p.id} value={p.id}>

                  {p.label}

                </MenuItem>

              ))}

            </Select>

            {activeProfile?.description && (

              <FormHelperText>{activeProfile.description}</FormHelperText>

            )}

          </FormControl>

          <FormControlLabel

            control={

              <Switch

                checked={config.enabled}

                onChange={(e) => setConfig((c) => ({ ...c, enabled: e.target.checked }))}

                disabled={config.profile_id === 'disabled'}

              />

            }

            label="이 시설에서 게이트웨이 연동 사용"

          />

          {activeProfile && activeProfile.fields.length > 0 && (

            <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>

              <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>

                <Typography variant="subtitle2" color="text.secondary">

                  프로필 파라미터

                </Typography>

                {config.profile_id !== 'disabled' && (

                  <Button

                    size="small"

                    startIcon={<SampleIcon />}

                    onClick={() => applySampleToForm(config.profile_id)}

                  >

                    현재 프로필 샘플 채우기

                  </Button>

                )}

              </Box>

              {activeProfile.fields.map(renderField)}

            </Box>

          )}

          <TextField

            fullWidth

            margin="dense"

            label="운영 메모"

            multiline

            minRows={2}

            value={config.notes}

            onChange={(e) => setConfig((c) => ({ ...c, notes: e.target.value }))}

            helperText="현장 담당자·제조사 연락처 등"

          />

          {activeProfile?.adapter_module && config.profile_id !== 'disabled' && (

            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>

              서버 어댑터: {activeProfile.adapter_module}

            </Typography>

          )}

          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>

            <Button

              variant="contained"

              startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}

              disabled={saving}

              onClick={handleSave}

            >

              게이트웨이 설정 저장

            </Button>

          </Box>

        </>

      )}

    </Paper>

  );

};



export default PoolWearableGatewaySettingsCard;


